import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  collection,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { authFetch } from '../utils/cloudFunctionsAuth';

// ---------------------------------------------------------------------------
// Direct Stripe invoice payments — captured by the stripeInvoicePaymentWebhook
// into the MAIN app Firestore `stripe_payments` collection. These are payments
// a customer made through the "Pay with Stripe" link on an invoice PDF. They are
// review-first: the owner applies each one to an invoice (which records a real
// ledger payment) or dismisses it. Server-only writes — the client only reads +
// calls the apply/dismiss HTTP endpoints.
// ---------------------------------------------------------------------------

const CF_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

export type StripePaymentStatus = 'pending' | 'applied' | 'dismissed';

export interface StripePayment {
  id: string;                                   // == Stripe checkout session id
  status: StripePaymentStatus;
  amount: number;                               // dollars
  currency: string;                             // e.g. 'AUD'
  email: string;
  name: string;
  paidAt: string;                               // YYYY-MM-DD (Adelaide)
  sessionId?: string;
  customFields?: { label: string; value: string }[];
  testMode?: boolean;
  appliedInvoiceId?: string | null;
  appliedInvoiceNumber?: string | null;
  createdAt?: Timestamp;
}

interface ApplyResult { ok: boolean; error?: string; invoiceNumber?: string | null; amount?: number }
interface DismissResult { ok: boolean; error?: string }

interface StripePaymentsContextType {
  stripePayments: StripePayment[];
  pending: StripePayment[];
  loading: boolean;
  applyPayment: (stripePaymentId: string, invoiceId: string) => Promise<ApplyResult>;
  dismissPayment: (stripePaymentId: string) => Promise<DismissResult>;
}

const StripePaymentsContext = createContext<StripePaymentsContextType>({
  stripePayments: [],
  pending: [],
  loading: true,
  applyPayment: async () => ({ ok: false }),
  dismissPayment: async () => ({ ok: false }),
});

function mapDoc(id: string, d: any): StripePayment {
  return {
    id,
    status: (d?.status as StripePaymentStatus) || 'pending',
    amount: Number(d?.amount) || 0,
    currency: String(d?.currency || 'AUD'),
    email: String(d?.email || ''),
    name: String(d?.name || ''),
    paidAt: String(d?.paidAt || ''),
    sessionId: d?.sessionId,
    customFields: Array.isArray(d?.customFields) ? d.customFields : [],
    testMode: d?.testMode === true,
    appliedInvoiceId: d?.appliedInvoiceId ?? null,
    appliedInvoiceNumber: d?.appliedInvoiceNumber ?? null,
    createdAt: d?.createdAt,
  };
}

export const StripePaymentsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stripePayments, setStripePayments] = useState<StripePayment[]>([]);
  const [loading, setLoading] = useState(true);
  const initialLoadDone = useRef(false);

  useEffect(() => {
    let unsub: (() => void) | null = null;
    try {
      const db = getFirestore();
      const q = query(collection(db, 'stripe_payments'), orderBy('createdAt', 'desc'));
      unsub = onSnapshot(
        q,
        (snap) => {
          setStripePayments(snap.docs.map((d) => mapDoc(d.id, d.data())));

          if (!initialLoadDone.current) {
            initialLoadDone.current = true;
            setLoading(false);
            return;
          }
          // Toast only genuinely new PENDING captures (not our own apply/dismiss
          // status flips, which arrive as 'modified').
          snap.docChanges().forEach((change) => {
            if (change.type === 'added') {
              const p = mapDoc(change.doc.id, change.doc.data());
              if (p.status === 'pending') {
                const who = p.name || p.email || 'a customer';
                toast.info(
                  `💳 Stripe payment received — ${who} ($${p.amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })})`,
                  { autoClose: 8000 },
                );
              }
            }
          });
        },
        (err) => {
          // Non-admins are denied by rules — degrade quietly to an empty feed.
          console.warn('[StripePayments] listener error:', err);
          setLoading(false);
        },
      );
    } catch (err) {
      console.warn('[StripePayments] init failed:', err);
      setLoading(false);
    }
    return () => { if (unsub) unsub(); };
  }, []);

  const applyPayment = useCallback(async (stripePaymentId: string, invoiceId: string): Promise<ApplyResult> => {
    try {
      const res = await authFetch(`${CF_BASE}/applyStripePaymentHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stripePaymentId, invoiceId }),
      });
      const data = await res.json();
      if (!data.ok) return { ok: false, error: data.error || 'Could not apply payment' };
      return { ok: true, invoiceNumber: data.invoiceNumber, amount: data.amount };
    } catch (e: any) {
      return { ok: false, error: e?.message || 'Could not apply payment' };
    }
  }, []);

  const dismissPayment = useCallback(async (stripePaymentId: string): Promise<DismissResult> => {
    try {
      const res = await authFetch(`${CF_BASE}/dismissStripePaymentHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stripePaymentId }),
      });
      const data = await res.json();
      if (!data.ok) return { ok: false, error: data.error || 'Could not dismiss payment' };
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.message || 'Could not dismiss payment' };
    }
  }, []);

  const pending = stripePayments.filter((p) => p.status === 'pending');

  return (
    <StripePaymentsContext.Provider value={{ stripePayments, pending, loading, applyPayment, dismissPayment }}>
      {children}
    </StripePaymentsContext.Provider>
  );
};

export const useStripePayments = () => useContext(StripePaymentsContext);
